// Shared Edit rules for the vendor item write surfaces (HUBX-8477, from HUBX-8474).
//
// The two halves of an Edit round trip: seeding a form from an ItemResponse, and turning
// that form back into the full-state PUT body. Moved out of ItemWriteModalComponent
// because the modal is no longer the only caller: the multi-row save (HUBX-8477) builds a
// form per edited row to run the shared validators and to produce the same body the
// modal would, and the simple and detailed rows (HUBX-8480, HUBX-8481) will seed and
// build through here too. A second copy of either half is a body that drifts.
//
// Pure functions over a FormGroup built by createItemWriteForm. No DI, no component
// state: every fact a rule needs arrives as an argument.

import { FormGroup, UntypedFormArray } from '@angular/forms';
import {
  ICreateItemAttribute,
  ICreateItemDiscountQty,
  IEditItemRequest,
  ILookupAttribute,
  ILookupCategory,
  ItemResponse
} from '../item.models';
import { editAttributes } from './item-write-attributes.rules';
import { IAttributeLookups } from './item-write-concurrency';
import * as writeAccess from './item-write-writability.rules';

/** Every lookup the seeding reads: the category tree (the two selects are resolved by
 *  walking it) and the three attributes (matched by id). */
export interface IEditLookups extends IAttributeLookups {
  categories: ILookupCategory[];
}

/** Everything buildEditRequest needs besides the form values.
 *
 *  `origin` is the state the form was built against — the baseline the status rules
 *  compare with and the source of every echo-only member (tags, free-form attributes).
 *  Once a conflict is resolved it is the item as re-read, not the original GET (HUBX-8466).
 *
 *  The tokens and the archived flag are separate from `origin` on purpose. The modal
 *  holds them as fields that move on their own schedule — the flag is an intent the
 *  Archive action sets, the tokens move only when a conflict is resolved — and a row
 *  holds them on its edited entry. Neither is "whatever origin says". */
export interface IEditContext {
  origin?: ItemResponse;
  lookups: IAttributeLookups;
  writableFields: string[];
  /** ADMIN / VENDORMGR. Decides whether sale pricing may travel at all (§8.3). */
  pricing: boolean;
  isArchived: boolean;
  expectedRowVersion: string;
  expectedItemRowVersion: string;
}

/** Edit seeding (§4.2, HUBX-8388): FULL STATE. Every member the body carries is filled
 *  from the item, money included — the opposite of Clone's seeding. The contract is that
 *  the response re-submitted verbatim produces an empty delta, so anything left at a
 *  build-form default would read as a real change the user never made.
 *
 *  What a Seller does NOT receive (unitPrice, discountQtys — §8.3 withholds them) simply
 *  stays null/empty, and buildEditRequest omits it, which is exactly what PricingPolicy
 *  expects from that role.
 *
 *  Held OUTSIDE the form, and so not seeded here: the archived flag and the two tokens
 *  (see IEditContext). Returns the sub-category list the chosen main category exposes, or
 *  undefined when the category did not resolve — the caller owns that list. */
export function seedEditForm(fg: FormGroup, t: ItemResponse, lookups: IEditLookups): ILookupCategory[] | undefined {
  fg.patchValue({
    vendorItemCode: t.vendorItemCode,
    hubxItemCode: t.hubxItemCode ?? '',
    mpn: t.mpn ?? '',
    description: t.description ?? '',
    manufacturerId: t.manufacturerId ?? '',
    availability: t.availability,
    leadTimeDays: t.leadTimeDays ?? null,
    moq: t.minimumOrderQty,
    mxq: t.maximumOrderQty ?? null,
    cost: t.unitPurchasePrice ?? null,
    restrictions: t.restrictions ?? '',
    specialNotes: t.specialNotes ?? '',
    // The PROJECTION, straight from the server (SkuStatusProjection). Deliberately not
    // recomputed here from the three axes: the rule folds in inventory and IsArchived
    // and has its own precedence, and a second copy of it in the client is a second
    // copy free to drift.
    skuStatus: t.effectiveStatus,
    todaysDeals: t.todaysDeals,
    discountQtyMarginPercent: t.discountQtyMarginPercent ?? null
  });

  // Withheld from a Seller, so guarded: patching undefined would leave the control
  // untouched anyway, but being explicit keeps the role rule visible here.
  if (t.unitPrice != null) {
    fg.controls['unitPrice'].setValue(t.unitPrice);
  }
  if (t.discountQtys?.length) {
    const tiers = fg.get('discountQtys') as UntypedFormArray;
    t.discountQtys.slice(0, 3).forEach((tier, i) =>
      tiers.at(i).patchValue({ fromQty: tier.fromQty, price: tier.price }));
  }

  // The EXW select binds option values with [value] (strings); buildEditRequest coerces
  // back with Number().
  fg.controls['exwId'].setValue(String(t.exwId));
  const subCategories = seedCategory(fg, lookups.categories, t.categoryId ?? t.masterCategoryId);
  seedAttributes(fg, lookups, t.attributes);
  return subCategories;
}

/** Resolve the effective categoryId (leaf) back to the main + optional sub the two selects
 *  expect, walking the one-level tree. Returns the children the main node exposes.
 *  Unresolvable ids (e.g. a legacy category no longer in the lookup tree) leave the
 *  selects blank so the required validator makes the user pick, and return undefined. */
export function seedCategory(
  fg: FormGroup,
  categories: ILookupCategory[],
  categoryId?: string | null
): ILookupCategory[] | undefined {
  if (!categoryId) {
    return undefined;
  }
  const topLevel = categories.find(c => c.categoryId === categoryId);
  if (topLevel) {
    fg.controls['categoryMainId'].setValue(categoryId);
    fg.controls['categoryId'].setValue(categoryId);
    return topLevel.children ?? [];
  }
  const parent = categories.find(c => c.children?.some(ch => ch.categoryId === categoryId));
  if (parent) {
    fg.controls['categoryMainId'].setValue(parent.categoryId);
    fg.controls['categoryId'].setValue(categoryId);
    return parent.children ?? [];
  }
  return undefined;
}

/** The Main / Sub a category id resolves to in the one-level `/lookups/categories` tree: a
 *  root is Main with no Sub, a child is Sub under its parent as Main, anything else is
 *  neither. What a row draws its category pair from — pass the EFFECTIVE id,
 *  `categoryId ?? masterCategoryId` (ADR-0007) — and what the modal's `Main / Sub` path is
 *  written from, so both walk the tree the same way (HUBX-8480). */
export function categoryPair(
  categories: ILookupCategory[],
  categoryId: string | null | undefined
): { main: ILookupCategory | null; sub: ILookupCategory | null } {
  if (categoryId) {
    for (const top of categories) {
      if (top.categoryId === categoryId) {
        return { main: top, sub: null };
      }
      const child = top.children?.find(c => c.categoryId === categoryId);
      if (child) {
        return { main: top, sub: child };
      }
    }
  }
  return { main: null, sub: null };
}

/** The Main category select picked `mainId`: the Sub list it exposes, and what `categoryId`
 *  becomes — '' when the main has subs (one must be picked, and the required validator says
 *  so), the main itself when it is a leaf. A main the tree does not know is taken as the
 *  leaf, as the modal always did.
 *
 *  Moved out of ItemWriteModalComponent.onMainCategoryChange for the listing rows
 *  (HUBX-8480 / HUBX-8481), whose category pair becomes live: the row and the popup must
 *  answer this the same way. */
export function categoryForMainChoice(
  categories: ILookupCategory[],
  mainId: string
): { subCategories: ILookupCategory[]; categoryId: string } {
  const main = categories.find(c => c.categoryId === mainId);
  const subCategories = main?.children ?? [];
  return { subCategories, categoryId: subCategories.length ? '' : mainId };
}

/** Seed the three modelled attributes by matching the item's attributeId to the resolved
 *  Condition / Warranty / Packaging lookup; the control holds the value CODE (what the
 *  option binds). Attributes outside the three have no control — on Edit they travel by
 *  echo (editAttributes), never through the form. */
export function seedAttributes(fg: FormGroup, lookups: IAttributeLookups, attributes: ICreateItemAttribute[]): void {
  seedAttribute(fg, 'condition', lookups.condition, attributes);
  seedAttribute(fg, 'warranty', lookups.warranty, attributes);
  seedAttribute(fg, 'packaging', lookups.packaging, attributes);
}

/** One of the three, alone — what a resolved conflict takes as Current for one attribute. */
export function seedAttribute(
  fg: FormGroup,
  control: string,
  lookup: ILookupAttribute | undefined,
  attributes: ICreateItemAttribute[]
): void {
  if (!lookup) {
    return;
  }
  const match = (attributes ?? []).find(a => a.attributeId === lookup.id);
  if (match?.value) {
    fg.controls[control].setValue(match.value);
  }
}

/** Map the form to the Edit contract (IEditItemRequest). FULL STATE: every member travels,
 *  including the ones the user did not touch, because the server derives what changed by
 *  diffing against what it holds. The optionals behave as on Create for the same reason —
 *  an empty Special Notes is a removal, and omitting the member is how a removal is
 *  expressed.
 *
 *  `v` is getRawValue(), not value: a Seller's disabled controls (unitPrice, XID, the DQ
 *  tiers) are dropped from `value`, and on Create that is exactly right — but here the XID
 *  must round-trip or the server sees the Seller clearing it. The two the Seller genuinely
 *  must not carry are filtered explicitly below, by role, rather than relying on a
 *  disabled flag to mean two different things. */
export function buildEditRequest(v: { [key: string]: any }, ctx: IEditContext): IEditItemRequest {
  const request: IEditItemRequest = {
    vendorItemCode: (v['vendorItemCode'] ?? '').trim(),
    mpn: (v['mpn'] ?? '').trim(),
    description: (v['description'] ?? '').trim(),
    manufacturerId: v['manufacturerId'],
    categoryId: v['categoryId'],
    availability: Number(v['availability']),
    leadTimeDays: Number(v['leadTimeDays']),
    exwId: Number(v['exwId']),
    // The three rendered attributes plus an echo of every stored one the form does not
    // render (HUBX-8475) — the PUT deletes whatever this leaves out. From `origin`, like
    // tags, so a resolved conflict moves the echo onto the item as re-read.
    attributes: editAttributes(v, ctx.lookups, ctx.origin?.attributes),
    // The status intent (ADR-0009 Rule 9): the collapsed control's value as it stands — the
    // pick, or the effectiveStatus it was seeded with when nobody touched it. The server
    // resolves it into the axes from the caller and the stored state (un-pause, approval,
    // manual reactivation), so the client no longer maps it. An untouched control is an
    // echo and moves nothing.
    status: v['skuStatus'],
    isArchived: ctx.isArchived,
    expectedRowVersion: ctx.expectedRowVersion,
    expectedItemRowVersion: ctx.expectedItemRowVersion
  };

  const xid = (v['hubxItemCode'] ?? '').trim();
  if (xid) { request.hubxItemCode = xid; }
  if (v['cost'] != null && v['cost'] !== '') { request.unitPurchasePrice = Number(v['cost']); }
  if (v['moq'] != null && v['moq'] !== '') { request.minimumOrderQty = Number(v['moq']); }
  if (v['mxq'] != null && v['mxq'] !== '') { request.maximumOrderQty = Number(v['mxq']); }

  // Sale pricing is ADMIN/VM-owned and §8.3 does not even emit it to a Seller, so a
  // Seller's body must not carry it: PricingPolicy rejects PRESENCE, not a divergence.
  // Keyed on the role rather than on emptiness — an operator clearing the tiers is a real
  // removal and must still travel as an absent member.
  if (ctx.pricing) {
    if (v['unitPrice'] != null && v['unitPrice'] !== '') { request.unitPrice = Number(v['unitPrice']); }
    const discountQtys: ICreateItemDiscountQty[] = (v['discountQtys'] ?? [])
      .filter((d: any) => d && d.fromQty != null && d.fromQty !== '' && d.price != null && d.price !== '')
      .map((d: any) => ({ fromQty: Number(d.fromQty), price: Number(d.price) }));
    if (discountQtys.length) { request.discountQtys = discountQtys; }
  }

  const specialNotes = (v['specialNotes'] ?? '').trim();
  if (specialNotes) { request.specialNotes = specialNotes; }
  const restrictions = (v['restrictions'] ?? '').trim();
  if (restrictions) { request.restrictions = restrictions; }

  // From `origin` rather than the item first read, which is what makes a tags change by
  // the other writer carry over once a conflict is resolved: nothing in these forms edits
  // tags, so the body carries an echo rather than an intent, and moving the echo onto the
  // item as re-read IS the resolution (HUBX-8439, HUBX-8466).
  const tags = ctx.origin?.tags;
  if (tags?.length) { request.tags = tags; }

  // Today's Deals: the one member here that is NOT full state. Absent means "do not
  // touch", so a caller the server did not grant it simply omits it — echoing the value
  // read would be a Seller moving a flag it may not move (TODAYS_DEALS_NOT_ALLOWED), and
  // omitting it on a caller who MAY move it would silently drop the toggle.
  if (writeAccess.mayCurate(ctx.writableFields)) {
    request.todaysDeals = !!v['todaysDeals'];
  }

  return request;
}

/**
 * The other half of seedEditForm, for the listing rows (HUBX-8480 / HUBX-8481): fold a row's
 * form back into the complete ItemResponse the page's edited-row store holds (HUBX-8479).
 * Under a full-state PUT the edited state of a row IS an ItemResponse, so the store needs no
 * record of which fields moved; the save seeds a form from it again and builds the body the
 * modal would (prepareRowSave).
 *
 * Untouched, `seedEditForm` then this is the IDENTITY, member for member. That is
 * load-bearing: the store calls a row edited when it differs from what was stored, so any
 * member this rewrites without the user moving it (a null held as '' by the form, a number
 * held as text, the override category shown as the master's, attributes re-ordered) would
 * mark a row the user never touched and re-send it. So each member is written only when its
 * normalised value differs from `current`'s; otherwise `current`'s own value stays, null,
 * absent or verbatim.
 *
 * - Members no form edits come from `current` unchanged: ids, vendorItemCode, both tokens,
 *   isArchived, tags, writableFields, the three axes, masterCategoryId.
 * - The collapsed status control is carried as `effectiveStatus`, which is how IBatchRow
 *   carries it; the body sends it as `status` and the server derives the axes.
 * - Sale pricing (`unitPrice`, `discountQtys`) is written only where `current` has the
 *   member: §8.3 OMITS it for a Seller, and absent is a different fact from null. The price
 *   is also written when writableFields names it (HUBX-8480): the response omits a null
 *   unitPrice for every role, so an operator's item HUBX has not priced yet arrives without
 *   the member, and without this the price typed in the row never reached the store.
 * - A required member left blank (availability, exwId) or a main category with subs and no
 *   sub picked stays blank, so seeding it again leaves the control empty and the save's
 *   pre-flight refuses the row — rather than quietly saving the stored or the master value.
 * - A blank MOQ is 1: the server's default for an absent member, which is what the modal gets
 *   by omitting it.
 * - Attributes keep the stored ORDER: the three rendered ones take their control's value in
 *   place (or keep the stored one when the control is empty, as editAttributes does), a
 *   rendered one the item did not have is appended, and every other stored attribute stays as
 *   it is (HUBX-8475).
 */
export function editedItemFromForm(v: { [key: string]: any }, current: ItemResponse, lookups: IAttributeLookups): ItemResponse {
  const edited: ItemResponse = {
    ...current,
    attributes: (current.attributes ?? []).map(a => ({ ...a })),
    tags: [...(current.tags ?? [])],
    writableFields: [...(current.writableFields ?? [])]
  };
  if (current.discountQtys) {
    edited.discountQtys = current.discountQtys.map(d => ({ ...d }));
  }

  const text = (key: 'hubxItemCode' | 'mpn' | 'description' | 'specialNotes' | 'restrictions', control: string) => {
    const typed = String(v[control] ?? '').trim();
    if (typed !== String(current[key] ?? '').trim()) {
      edited[key] = typed || null;
    }
  };
  text('hubxItemCode', 'hubxItemCode');
  text('mpn', 'mpn');
  text('description', 'description');
  text('specialNotes', 'specialNotes');
  text('restrictions', 'restrictions');

  const manufacturerId = v['manufacturerId'] || null;
  if (manufacturerId !== (current.manufacturerId ?? null)) {
    edited.manufacturerId = manufacturerId;
  }

  // The form shows the EFFECTIVE category (the override, else the master's — ADR-0007); an
  // unchanged choice keeps the raw override, null included.
  const categoryId: string = v['categoryId'] ?? '';
  if (categoryId !== (current.categoryId ?? current.masterCategoryId ?? '')) {
    edited.categoryId = categoryId;
  }

  const num = (value: unknown): number | null => value == null || value === '' ? null : Number(value);
  const setNumber = <K extends 'availability' | 'exwId' | 'leadTimeDays' | 'maximumOrderQty' | 'unitPurchasePrice' | 'minimumOrderQty' | 'unitPrice'>(key: K, value: number | null) => {
    if (value !== (current[key] ?? null)) {
      // A blank REQUIRED member is held as null on purpose (see above); the contract types
      // it as number because a stored item always has one.
      (edited as any)[key] = value;
    }
  };
  setNumber('availability', num(v['availability']));
  setNumber('exwId', num(v['exwId']));
  setNumber('leadTimeDays', num(v['leadTimeDays']));
  setNumber('maximumOrderQty', num(v['mxq']));
  setNumber('unitPurchasePrice', num(v['cost']));
  setNumber('minimumOrderQty', num(v['moq']) ?? 1);

  if ('unitPrice' in current || (current.writableFields ?? []).includes('unitPrice')) {
    setNumber('unitPrice', num(v['unitPrice']));
  }
  if (current.discountQtys) {
    const tiers: ICreateItemDiscountQty[] = (v['discountQtys'] ?? [])
      .filter((d: any) => d && d.fromQty != null && d.fromQty !== '' && d.price != null && d.price !== '')
      .map((d: any) => ({ fromQty: Number(d.fromQty), price: Number(d.price) }));
    const shown = current.discountQtys.slice(0, 3).map(d => ({ fromQty: d.fromQty, price: d.price }));
    if (JSON.stringify(tiers) !== JSON.stringify(shown)) {
      edited.discountQtys = tiers;
    }
  }

  const attributes = orderedEditAttributes(v, lookups, current.attributes ?? []);
  if (JSON.stringify(attributes) !== JSON.stringify(current.attributes ?? [])) {
    edited.attributes = attributes;
  }

  const skuStatus = v['skuStatus'] || current.effectiveStatus;
  if (skuStatus !== current.effectiveStatus) {
    edited.effectiveStatus = skuStatus;
  }
  if (!!v['todaysDeals'] !== current.todaysDeals) {
    edited.todaysDeals = !!v['todaysDeals'];
  }

  return edited;
}

/**
 * A half-typed discount tier, carried in the row's edit.
 *
 * editedItemFromForm keeps only complete tiers, so without this a half-typed one is not part
 * of the item at all. The edit has to carry it: the save's pre-flight (prepareRowSave)
 * re-validates the edit, not the row's form, so without the tier it would save the rest and
 * drop the tier, or delete a stored tier whose other half was cleared. And it makes the row
 * edited on its own (HUBX-8466): a tier the row shows as refused is unsaved input, so it counts
 * in Unsaved Listings and blocks Update All like any other refused edit. Carried at its slot
 * with the missing half null, seeding it again shows the same tier and incompletePair refuses
 * the row under the same DQ number.
 *
 * Returns `edited` itself when no tier is half-typed, or when the caller has no sale pricing.
 */
export function carryIncompleteTiers(edited: ItemResponse, v: { [key: string]: any }): ItemResponse {
  const typed = (x: unknown) => x != null && x !== '';
  const raw: any[] = v['discountQtys'] ?? [];
  if (!edited.discountQtys || !raw.some(d => d && typed(d.fromQty) !== typed(d.price))) {
    return edited;
  }
  const slots = raw.map(d => ({
    fromQty: typed(d?.fromQty) ? Number(d.fromQty) : null,
    price: typed(d?.price) ? Number(d.price) : null
  }));
  while (slots.length && slots[slots.length - 1].fromQty === null && slots[slots.length - 1].price === null) {
    slots.pop();
  }
  // The nulls live only in the edit: the pre-flight refuses them before any body is built.
  return { ...edited, discountQtys: slots as unknown as ICreateItemDiscountQty[] };
}

/** editAttributes' rule, in the stored order (see editedItemFromForm). */
function orderedEditAttributes(
  v: { [key: string]: any },
  lookups: IAttributeLookups,
  stored: ICreateItemAttribute[]
): ICreateItemAttribute[] {
  const rendered = new Map<string, string | undefined>();
  for (const key of ['condition', 'warranty', 'packaging'] as const) {
    const lookup = lookups[key];
    if (lookup) {
      rendered.set(lookup.id, v[key] || undefined);
    }
  }

  const attributes = stored.map(a => {
    const value = rendered.get(a.attributeId);
    return value ? { ...a, value } : { ...a };
  });
  for (const [attributeId, value] of rendered) {
    if (value && !stored.some(a => a.attributeId === attributeId)) {
      attributes.push({ attributeId, value });
    }
  }
  return attributes;
}
